import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FadeUp, MaskedLines } from "@/components/Reveal";

const shades = [
  { name: "Pearl White", hex: "#F6F4EF", family: "The Whites", ink: true },
  { name: "Ivory Silk", hex: "#F3EADA", family: "The Whites", ink: true },
  { name: "Champagne", hex: "#E8D6B0", family: "The Whites", ink: true },
  { name: "Ballet Blush", hex: "#F4C6D7", family: "The Romantics", ink: true },
  { name: "Rose Quartz", hex: "#E8A0BF", family: "The Romantics", ink: true },
  { name: "Coral Kiss", hex: "#F26D7D", family: "The Romantics" },
  { name: "Liquid Gold", hex: "#D4AF37", family: "The Metallics", ink: true },
  { name: "Rose Gold", hex: "#B76E79", family: "The Metallics" },
  { name: "Sterling Silver", hex: "#C9CCD3", family: "The Metallics", ink: true },
  { name: "Midnight Navy", hex: "#1A2340", family: "The Dramatics" },
  { name: "Onyx Noir", hex: "#141414", family: "The Dramatics" },
  { name: "Emerald Velvet", hex: "#0E5F45", family: "The Dramatics" },
];

export default function ColorStory() {
  const [shade, setShade] = useState(shades[3]);

  return (
    <section id="shades" data-testid="color-story-section" className="relative bg-ivory py-32 sm:py-44 overflow-hidden">
      <div className="absolute -bottom-24 left-0 w-[460px] h-[460px] rounded-full bg-coral/10 blur-[120px]" aria-hidden="true" />
      <div className="max-w-[1200px] mx-auto px-6 sm:px-10 relative">
        <FadeUp className="mb-6">
          <p className="text-[12px] tracking-[0.45em] uppercase text-coral">Wrap Services · The Colour Story</p>
        </FadeUp>
        <h2 className="font-serif font-light text-ink leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-6">
          <MaskedLines inView lines={[<>Pick your <span className="italic text-coral">perfect shade</span></>]} />
        </h2>
        <FadeUp delay={0.1} className="mb-14 sm:mb-16">
          <p className="text-ink/60 text-base max-w-xl">
            Every single-colour wrap is cut, laid and hand-finished in the shade you choose.
            Tap through the deck and imagine it under your chandelier.
          </p>
        </FadeUp>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
          <FadeUp>
            <div
              data-testid="shade-preview"
              className="relative h-[320px] sm:h-[420px] rounded-[2rem] overflow-hidden shadow-[0_40px_90px_-30px_rgba(45,40,51,0.4)]"
            >
              <AnimatePresence>
                <motion.div
                  key={shade.name}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6 }}
                  className="absolute inset-0"
                  style={{ background: shade.hex }}
                />
              </AnimatePresence>
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: "linear-gradient(115deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.08) 34%, transparent 55%, rgba(0,0,0,0.12) 100%)" }}
              />
              <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(10,11,26,0.22), transparent)" }} />
              <div className="absolute bottom-6 left-7">
                <p data-testid="shade-name-label" className={`font-serif text-3xl sm:text-4xl ${shade.ink ? "text-ink" : "text-ivory"}`}>
                  {shade.name}
                </p>
                <p className={`text-[11px] tracking-[0.35em] uppercase mt-1 ${shade.ink ? "text-ink/60" : "text-ivory/70"}`}>
                  {shade.family} · Matte wrap finish
                </p>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.15} className="flex flex-col">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 sm:gap-4">
              {shades.map((s, i) => (
                <button
                  key={s.name}
                  data-testid={`shade-chip-${i}`}
                  onClick={() => setShade(s)}
                  className={`group relative h-20 sm:h-24 rounded-xl overflow-hidden transition-transform duration-300 hover:-translate-y-1 ${
                    shade.name === s.name ? "ring-2 ring-offset-2 ring-coral ring-offset-ivory" : ""
                  }`}
                  style={{ background: s.hex, boxShadow: "inset 0 0 0 1px rgba(45,40,51,0.1)" }}
                  aria-label={s.name}
                >
                  <span
                    className={`absolute bottom-1.5 left-2 right-2 text-left text-[10px] tracking-wide font-medium truncate ${
                      s.ink ? "text-ink/70" : "text-ivory/85"
                    }`}
                  >
                    {s.name}
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-auto pt-10">
              <p className="font-quote italic text-ink/70 text-lg mb-6">
                Don't see your dream shade? We colour-match anything — invitations, bridesmaid silk, even the frosting.
              </p>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
